import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Form, FormGroup, Input, Button, Label
} from "reactstrap";

import { API_BASE_URL } from "../config/api";

const BASE_URL = API_BASE_URL;

const normalizeGameLevelForDisplay = (level = {}, index = 0) => ({
  id: level?.id ?? null,
  sort_order: Number.isFinite(Number(level?.sort_order)) ? Number(level.sort_order) : index + 1,
  name_en: String(level?.label_en ?? level?.name_en ?? level?.label ?? level?.name ?? "").trim(),
  name_ar: String(level?.label_ar ?? level?.name_ar ?? "").trim(),
  icon_path: String(level?.icon_path ?? level?.icon ?? "").trim(),
  is_active: Number(level?.is_active ?? 1) === 0 ? 0 : 1
});

const emptyRules = {
  play_levels: "",
  play_levels_ar: "",
  choose_level: "",
  choose_level_ar: "",
  hall_navigation: "",
  hall_navigation_ar: "",
  progress_through_halls: "",
  progress_through_halls_ar: "",
  hints_to_help: "",
  hints_to_help_ar: "",
  use_hints: "",
  use_hints_ar: "",
  end_game_choices: "",
  end_game_choices_ar: "",
  complete_halls: "",
  complete_halls_ar: "",
  start_game: "",
  start_game_ar: "",
  hall_label: "",
  hall_label_ar: "",
  halls_list: "",
  halls_list_ar: "",
  level1_name: "",
  level1_name_ar: "",
  level2_name: "",
  level2_name_ar: "",
  level3_name: "",
  level3_name_ar: "",
  map_image: "",
  level1_icon: "",
  level2_icon: "",
  level3_icon: "",
  levels: []
};

function GameRules() {
  const [rules, setRules] = useState({ ...emptyRules });
  const [gameLevels, setGameLevels] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);

  const notify = (setter, msg, t = 3500) => {
    setter(msg);
    setTimeout(() => setter(""), t);
  };

  const fetchRules = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/game/rules?all=1`);
      const nextRules = { ...emptyRules, ...(res.data?.data || {}) };
      setRules(nextRules);
    } catch {
      notify(setError, "Error fetching rules.");
    }
  }, []);

  const fetchLevelsForDisplay = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/game/levels?all=1`, {
        headers: { "Accept-Language": "en" }
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setGameLevels(list.map((level, index) => normalizeGameLevelForDisplay(level, index)));
    } catch {
      notify(setError, "Error fetching levels.");
      setGameLevels([]);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchLevelsForDisplay();
  }, [fetchRules, fetchLevelsForDisplay]);

  const updateRuleField = (key, value) => {
    setRules((prev) => ({ ...prev, [key]: value }));
  };

  const resolveImageUrl = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return `${BASE_URL}/${s}`;
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);
      const res = await axios.post(`${BASE_URL}/game/rules/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.success && res.data?.path) {
        return res.data.path;
      }
      notify(setError, "Upload failed.");
      return null;
    } catch {
      notify(setError, "Upload failed.");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const uploadRuleImage = async (file, key) => {
    const path = await uploadFile(file);
    if (path) {
      updateRuleField(key, path);
    }
  };

  const clearImage = (key) => {
    updateRuleField(key, "");
  };

  const saveRules = async (e) => {
    e.preventDefault();

    const payload = { ...rules };

    try {
      await axios.put(`${BASE_URL}/game/rules`, payload);
      notify(setSuccess, "Rules updated.");
      fetchRules();
    } catch {
      notify(setError, "Failed to update rules.");
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Game Rules</CardTitle>
            </CardHeader>
            <CardBody>
              {error && <div className="alert alert-danger">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <Form onSubmit={saveRules}>
                <Row form>
                  <Col md="6">
                    <h6>English</h6>
                    <FormGroup>
                      <Label>Play Levels (Title)</Label>
                      <Input value={rules.play_levels} onChange={(e) => updateRuleField("play_levels", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Choose Level</Label>
                      <Input value={rules.choose_level} onChange={(e) => updateRuleField("choose_level", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Hall Navigation (Title)</Label>
                      <Input value={rules.hall_navigation} onChange={(e) => updateRuleField("hall_navigation", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Progress Through Halls</Label>
                      <Input value={rules.progress_through_halls} onChange={(e) => updateRuleField("progress_through_halls", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Hints To Help (Title)</Label>
                      <Input value={rules.hints_to_help} onChange={(e) => updateRuleField("hints_to_help", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Use Hints</Label>
                      <Input value={rules.use_hints} onChange={(e) => updateRuleField("use_hints", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>End Game Choices (Title)</Label>
                      <Input value={rules.end_game_choices} onChange={(e) => updateRuleField("end_game_choices", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Complete Halls</Label>
                      <Input value={rules.complete_halls} onChange={(e) => updateRuleField("complete_halls", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Start Game (Button)</Label>
                      <Input value={rules.start_game} onChange={(e) => updateRuleField("start_game", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Hall Label</Label>
                      <Input value={rules.hall_label} onChange={(e) => updateRuleField("hall_label", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Halls List (one per line)</Label>
                      <Input type="textarea" rows="6" value={rules.halls_list} onChange={(e) => updateRuleField("halls_list", e.target.value)} />
                    </FormGroup>
                  </Col>

                  <Col md="6">
                    <h6>Arabic</h6>
                    <FormGroup>
                      <Label>Play Levels (Title)</Label>
                      <Input value={rules.play_levels_ar} onChange={(e) => updateRuleField("play_levels_ar", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Choose Level</Label>
                      <Input value={rules.choose_level_ar} onChange={(e) => updateRuleField("choose_level_ar", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Hall Navigation (Title)</Label>
                      <Input value={rules.hall_navigation_ar} onChange={(e) => updateRuleField("hall_navigation_ar", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Progress Through Halls</Label>
                      <Input value={rules.progress_through_halls_ar} onChange={(e) => updateRuleField("progress_through_halls_ar", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Hints To Help (Title)</Label>
                      <Input value={rules.hints_to_help_ar} onChange={(e) => updateRuleField("hints_to_help_ar", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Use Hints</Label>
                      <Input value={rules.use_hints_ar} onChange={(e) => updateRuleField("use_hints_ar", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>End Game Choices (Title)</Label>
                      <Input value={rules.end_game_choices_ar} onChange={(e) => updateRuleField("end_game_choices_ar", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Complete Halls</Label>
                      <Input value={rules.complete_halls_ar} onChange={(e) => updateRuleField("complete_halls_ar", e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Start Game (Button)</Label>
                      <Input value={rules.start_game_ar} onChange={(e) => updateRuleField("start_game_ar", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Hall Label</Label>
                      <Input value={rules.hall_label_ar} onChange={(e) => updateRuleField("hall_label_ar", e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Halls List (one per line)</Label>
                      <Input type="textarea" rows="6" value={rules.halls_list_ar} onChange={(e) => updateRuleField("halls_list_ar", e.target.value)} />
                    </FormGroup>
                  </Col>

                  <Col md="12">
                    <h6>Map</h6>
                    <FormGroup>
                      <Label>Map Image</Label>
                      <Input type="file" accept="image/*" onChange={(e) => uploadRuleImage(e.target.files?.[0], "map_image")} disabled={uploading} />
                      {rules.map_image && (
                        <div style={{ marginTop: 8 }}>
                          <img src={resolveImageUrl(rules.map_image)} alt="map" style={{ maxHeight: 120, display: "block" }} />
                          <Button color="danger" size="sm" type="button" style={{ marginTop: 6 }} onClick={() => clearImage("map_image")}>
                            Remove
                          </Button>
                        </div>
                      )}
                    </FormGroup>

                    <div style={{ marginTop: 24, marginBottom: 12 }}>
                      <div>
                        <h6 style={{ marginBottom: 4 }}>Levels</h6>
                        <div className="text-muted">Levels are managed from Game Levels screen. Display only.</div>
                      </div>
                    </div>
                    {!gameLevels.length && (
                      <div className="text-muted" style={{ marginBottom: 16 }}>
                        No levels found.
                      </div>
                    )}

                    {gameLevels.map((level, index) => (
                      <Card key={level.id || `stage-${index}`} style={{ marginBottom: 16 }}>
                        <CardBody>
                          <div style={{ marginBottom: 16 }}>
                            <h6 style={{ margin: 0 }}>{`Level ${index + 1}`}</h6>
                          </div>

                          <Row>
                            <Col md="6">
                              <div style={{ marginBottom: 12 }}>
                                <Label style={{ fontWeight: 600 }}>Level Name (EN)</Label>
                                <div>{level.name_en || "-"}</div>
                              </div>
                            </Col>
                            <Col md="6">
                              <div style={{ marginBottom: 12 }}>
                                <Label style={{ fontWeight: 600 }}>Level Name (AR)</Label>
                                <div>{level.name_ar || "-"}</div>
                              </div>
                            </Col>
                          </Row>

                          <div>
                            <Label style={{ fontWeight: 600 }}>Level Icon</Label>
                            {level.icon_path ? (
                              <div style={{ marginTop: 8 }}>
                                <img src={resolveImageUrl(level.icon_path)} alt={`stage-${index + 1}`} style={{ maxHeight: 60, display: "block" }} />
                              </div>
                            ) : (
                              <div className="text-muted">No icon uploaded.</div>
                            )}
                          </div>

                          <div style={{ marginTop: 12 }}>
                            <Label style={{ fontWeight: 600 }}>Active</Label>
                            <div>{Number(level.is_active) === 0 ? "No" : "Yes"}</div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </Col>
                </Row>

                <Button color="primary" type="submit">Save</Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default GameRules;
